/**
 * Date helpers for `<input type="date">` fields.
 *
 * An event date must be **today or in the future** — historical dates are
 * invalid (see the backend `event-date.util`). These helpers produce/compare
 * the `YYYY-MM-DD` strings that native date inputs use, in the user's *local*
 * timezone (which is what the input itself renders), so "today" is always
 * selectable and the `min` boundary lines up with what the user sees.
 */

/** Today's date as a `YYYY-MM-DD` string in the local timezone, for `min`. */
export function todayInputValue(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * True when a `YYYY-MM-DD` input value is strictly before today. Empty/blank
 * values are not "past" — required-ness is handled separately. The string form
 * is ISO-ordered, so a lexical comparison is a correct date comparison.
 */
export function isPastDateInput(value: string | undefined, now: Date = new Date()): boolean {
  return Boolean(value) && (value as string) < todayInputValue(now);
}

/** Current time as a `HH:MM` string in the local timezone. */
export function currentTimeInputValue(now: Date = new Date()): string {
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}
